/**
 * <s-welcome-popup> — opens the welcome offer dialog on page load.
 *
 * - Opens after `data-delay` seconds only on the shopper's first visit in that browser;
 *   it never auto-opens again (the floating tab still reopens it on demand).
 * - Subscribed shoppers never see it again on that browser.
 * - After a successful signup (`customer_posted=true` in the URL): silently applies the
 *   discount (`data-discount-url`, Shopify's /discount/CODE route sets the cart cookie),
 *   then swaps `[data-welcome-form]` for `[data-welcome-success]`. The code is never shown.
 * - Never auto-opens on the cart page (`data-no-auto-open`); the floating tab still works.
 * - In the theme editor (`data-preview`) it always opens with the signup form.
 * - After a signup error (`#contact_form` hash): reopens so the error is visible.
 */
const STORAGE_KEY = 'pureaura-welcome-popup';
const SUBSCRIBED_KEY = 'pureaura-welcome-subscribed';
const SEEN_KEY = 'pureaura-welcome-seen';

class SWelcomePopup extends HTMLElement {
  #timer = null;
  #ac = null;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const hideDays = Number(this.dataset.hideDays) || 0;
    const delayMs = (Number(this.dataset.delay) || 0) * 1000;

    dialog.addEventListener(
      'close',
      () => {
        try {
          if (hideDays > 0) localStorage.setItem(STORAGE_KEY, String(Date.now() + hideDays * 86400000));
        } catch (e) {
          /* storage unavailable */
        }
      },
      { signal: this.#ac.signal }
    );

    const params = new URLSearchParams(window.location.search);
    const posted = params.get('customer_posted') === 'true';
    const hasFormHash = window.location.hash === '#contact_form';

    if (posted) {
      this.#store(SUBSCRIBED_KEY, '1');
      this.#applyDiscount();
      this.#showSuccess();
      this.#cleanUrl(params);
      this.#open(dialog, 0);
      return;
    }

    if (hasFormHash && this.querySelector('[role="alert"]')) {
      this.#open(dialog, 0);
      return;
    }

    // In the theme editor the popup always opens with the signup form, so the merchant can see and edit it
    // even after testing a signup in this browser.
    if (this.hasAttribute('data-preview')) {
      this.#open(dialog, delayMs);
      return;
    }

    if (this.hasAttribute('data-no-auto-open') && this.#read(SUBSCRIBED_KEY) !== '1') return;

    if (this.#read(SUBSCRIBED_KEY) === '1') {
      // Subscribed: no auto-open. The floating tab opens the dialog in its success state
      // and re-applies the discount in case the cart cookie has expired.
      this.#showSuccess();
      dialog.addEventListener(
        'toggle',
        (e) => {
          if (e.newState === 'open') this.#applyDiscount();
        },
        { signal: this.#ac.signal }
      );
      return;
    }
    // Auto-open only once per browser: after the first automatic showing it never opens by itself again.
    // The floating tab still lets shoppers reopen it on demand.
    if (this.#read(SEEN_KEY) === '1') return;
    if (hideDays > 0 && Number(this.#read(STORAGE_KEY)) > Date.now()) return;
    this.#store(SEEN_KEY, '1');
    this.#open(dialog, delayMs);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #showSuccess() {
    this.querySelector('[data-welcome-form]')?.setAttribute('hidden', '');
    this.querySelector('[data-welcome-success]')?.removeAttribute('hidden');
  }

  #applyDiscount() {
    const url = this.dataset.discountUrl;
    if (!url) return;
    fetch(url, { credentials: 'same-origin', redirect: 'follow' }).catch(() => {});
  }

  #cleanUrl(params) {
    params.delete('customer_posted');
    const query = params.toString();
    const next = window.location.pathname + (query ? `?${query}` : '');
    window.history.replaceState(window.history.state, '', next);
  }

  #store(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable */
    }
  }

  #read(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  #open(dialog, delayMs) {
    // Never pop up while the shopper is tapping, scrolling or typing: an interaction in the last
    // 2.5 s postpones the opening, so the first clicks on the page are never blocked by the dialog.
    if (this.hasAttribute('data-preview') || delayMs === 0) {
      this.#timer = setTimeout(() => {
        if (!dialog.open && document.body.contains(dialog)) dialog.showModal();
      }, delayMs);
      return;
    }
    let lastInteraction = 0;
    const mark = () => { lastInteraction = Date.now(); };
    ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach((type) =>
      window.addEventListener(type, mark, { passive: true, signal: this.#ac.signal })
    );
    const tryOpen = () => {
      if (dialog.open || !document.body.contains(dialog)) return;
      const idleFor = Date.now() - lastInteraction;
      if (idleFor < 2500) {
        this.#timer = setTimeout(tryOpen, 2500 - idleFor);
        return;
      }
      dialog.showModal();
    };
    this.#timer = setTimeout(tryOpen, delayMs);
  }
}

if (!customElements.get('s-welcome-popup')) {
  customElements.define('s-welcome-popup', SWelcomePopup);
}
